import React, { useEffect } from 'react';
import { Form, FormProps, Input, Modal, notification } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import useModal from 'stores/useModal';
import useBookingStore from 'stores/useBooking';

import { getMessageCode } from 'constants/validate';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import API from 'services/api';
import { refreshBookingList, refreshRoomMap } from 'hooks';
import { logEvent } from 'services/tracking/gaTracking';
import { useTranslation } from 'react-i18next';

function UndoCheckInModal() {
  const { t } = useTranslation();
  const MESSAGE_CODE = getMessageCode(t);
  const [form] = Form.useForm();
  const location = useLocation();
  const { isOpenUndoCheckIn, setIsOpenUndoCheckIn } = useModal();
  const { bookingLineId } = useBookingStore();

  const { mutateAsync: mutateUndoCheckIn, isPending } = useMutation({
    mutationFn: (params: { bookingLineId: number; reason: string }) =>
      API.booking.undoCheckIn(params.bookingLineId, params.reason)
  });

  useEffect(() => {
    if (!isOpenUndoCheckIn) {
      form.resetFields();
    }
  }, [form, isOpenUndoCheckIn]);

  const handleCancel = () => {
    setIsOpenUndoCheckIn(false);
  };

  const handleOk = () => {
    form.submit();
  };

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    if (bookingLineId) {
      try {
        await mutateUndoCheckIn({
          bookingLineId: bookingLineId,
          reason: values?.reason
        });

        setIsOpenUndoCheckIn(false);

        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_DETAIL, bookingLineId]
        });

        refreshBookingList(location);
        refreshRoomMap(location);

        logEvent({
          category: 'booking',
          action: 'undo_check_in',
          label: `BL ID: ${bookingLineId}`
        });
        notification.success({
          message: t('common.actions.undoCheckedInSuccessfully')
        });
      } catch (err: any) {
        notification.error({
          message: err.error || t('common.error')
        });
      }
    }
  };

  return (
    <Modal
      title={t('undoChekIn.title')}
      className="modal-undo-checkin"
      centered
      width={400}
      open={isOpenUndoCheckIn}
      okText={t('common.actions.confirm')}
      cancelText={t('common.actions.cancel')}
      okButtonProps={{ className: 'ant-btn-secondary' }}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={isPending}
      destroyOnClose
    >
      <Form
        form={form}
        name="undo-checkin-form"
        layout="vertical"
        style={{ width: '100%' }}
        initialValues={{
          reason: ''
        }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item
          name="reason"
          rules={[
            {
              required: true,
              message: MESSAGE_CODE.REQUIRED_REASON_FIX_ROOM
            }
          ]}
        >
          <Input.TextArea rows={5} placeholder={t('common.confirm.reason')} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default UndoCheckInModal;
